import React, { useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { auth } from '../../services/auth'
import { Screen } from '../../components/lease/Screen'
import { BrandHeading } from '../../components/lease/BrandHeading'
import { PrimaryButton } from '../../components/lease/PrimaryButton'

// Landlord fork of features/auth/AuthForm.jsx — same field set and JWT flow,
// but registers with is_landlord=true and lands in the landlord onboarding
// screen instead of the renter dashboard/legal gate. ?plan= (tier
// preselection from the marketing site) rides through to onboarding.
export function LandlordAuth() {
  const navigate = useNavigate()
  const [params] = useSearchParams()
  const plan = params.get('plan')
  const [mode, setMode] = useState(params.get('mode') === 'register' ? 'register' : 'login')
  const [email, setEmail] = useState('')
  const [fullName, setFullName] = useState('')
  const [password, setPassword] = useState('')
  const [address, setAddress] = useState('')
  const [phone, setPhone] = useState('')
  const [error, setError] = useState(null)
  const [busy, setBusy] = useState(false)

  const isRegister = mode === 'register'
  const inputClasses =
    'mt-1 w-full rounded-xl border-0 bg-white/70 p-3 text-slate-800 shadow-sm ring-1 ring-emerald-100 backdrop-blur placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 dark:bg-slate-800/60 dark:text-slate-100 dark:ring-slate-700'

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError(null)
    setBusy(true)
    try {
      if (isRegister) {
        await auth.register(email, fullName, password, address, phone, true)
      }
      await auth.login(email, password)
      navigate(plan ? `/onboarding?plan=${plan}` : '/onboarding')
    } catch (err) {
      setError(err.response?.data?.detail || err.message || 'Something went wrong. Try again.')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Screen>
      <header className="animate-fade-in text-center">
        <BrandHeading className="text-4xl">
          {isRegister ? 'Create your landlord account' : 'Sign in'}
        </BrandHeading>
        <p className="mt-3 text-slate-600 dark:text-slate-300">
          {isRegister
            ? 'Set up your LeaseCheck landlord account to continue.'
            : 'Welcome back to LeaseCheck for Landlords.'}
        </p>
      </header>

      <form
        onSubmit={handleSubmit}
        className="mt-8 animate-slide-in space-y-4 rounded-2xl bg-white/70 p-6 shadow-sm ring-1 ring-emerald-100 backdrop-blur dark:bg-slate-800/60 dark:ring-slate-700"
      >
        {isRegister && (
          <div>
            <label htmlFor="fullName" className="block text-sm font-semibold text-emerald-700 dark:text-emerald-400">
              Full name
            </label>
            <input
              id="fullName"
              type="text"
              required
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              className={inputClasses}
            />
          </div>
        )}

        {isRegister && (
          <>
            <div>
              <label htmlFor="address" className="block text-sm font-semibold text-emerald-700 dark:text-emerald-400">
                Business / mailing address
              </label>
              <textarea
                id="address"
                required
                rows={2}
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                placeholder={'Street\nCity, State ZIP'}
                className={inputClasses}
              />
            </div>
            <div>
              <label htmlFor="phone" className="block text-sm font-semibold text-emerald-700 dark:text-emerald-400">
                Phone
              </label>
              <input
                id="phone"
                type="tel"
                required
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                className={inputClasses}
              />
            </div>
          </>
        )}

        <div>
          <label htmlFor="email" className="block text-sm font-semibold text-emerald-700 dark:text-emerald-400">
            Email
          </label>
          <input
            id="email"
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={inputClasses}
          />
        </div>

        <div>
          <label htmlFor="password" className="block text-sm font-semibold text-emerald-700 dark:text-emerald-400">
            Password
          </label>
          <input
            id="password"
            type="password"
            required
            minLength={8}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className={inputClasses}
          />
        </div>

        {error && <p className="text-sm text-rose-600 dark:text-rose-400">{error}</p>}

        <PrimaryButton type="submit" disabled={busy}>
          {busy ? 'Please wait…' : isRegister ? 'Create account' : 'Sign in'}
        </PrimaryButton>
      </form>

      <button
        type="button"
        onClick={() => {
          setMode(isRegister ? 'login' : 'register')
          setError(null)
        }}
        className="mt-6 w-full text-center text-sm font-semibold text-emerald-700 underline dark:text-emerald-400"
      >
        {isRegister ? 'Have an account? Sign in' : 'New here? Create an account'}
      </button>
    </Screen>
  )
}

export default LandlordAuth
